import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ContentChild, OnInit } from '@angular/core';
import { scaleBand, scaleLinear } from 'd3-scale';
import { BaseChartComponent, calculateViewDimensions, ColorHelper } from '@swimlane/ngx-charts';
import { Userpilot } from 'userpilot';

@Component({
  selector: 'ngx-charts-custom-bar-vertical',
  templateUrl: 'custom-bar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: []
})
export class CustomBarComponent extends BaseChartComponent implements OnInit{
  colors: ColorHelper;
  yDomain: any[];
  xDomain: any;

  @Input() legend = false;
  @Input() legendTitle = 'Legend';
  @Input() legendContents;
  @Input() xAxis;
  @Input() yAxis;
  @Input() showXAxisLabel;
  @Input() showYAxisLabel;
  @Input() xAxisLabel;
  @Input() yAxisLabel;
  @Input() tooltipDisabled = false;
  @Input() gradient;
  @Input() showGridLines = true;
  @Input() activeEntries = [];
  @Input() schemeType = null;
  @Input() xAxisTickFormatting;
  @Input() yAxisTickFormatting;
  @Input() barPadding = 8;
  @Input() roundDomains = false;
  @Input() roundEdges = true;
  @Output() activate = new EventEmitter();
  @Output() deactivate = new EventEmitter();
  tooltipTemplate: ContentChild;

  margin = [10, 20, 10, 20];
  xAxisHeight = 0;
  yAxisWidth = 0;

  dims;
  xScale;
  yScale;
  legendOptions;
  transform;

  ngOnInit() {
    Userpilot.reload();
  }



  update() {
    super.update();
    this.dims = calculateViewDimensions({
      width: this.width,
      height: this.height,
      margins: this.margin,
      showXAxis: this.xAxis,
      showYAxis: this.yAxis,
      xAxisHeight: this.xAxisHeight,
      yAxisWidth: this.yAxisWidth,
      showXLabel: this.showXAxisLabel,
      showYLabel: this.showYAxisLabel,
      showLegend: this.legend,
      legendType: this.schemeType
    });
    this.xScale = this.getXScale();
    this.yScale = this.getYScale();
    this.setColors();
    this.legendOptions = this.getLegendOptions();
    this.transform = 'translate(' + this.dims.xOffset + ' , ' + this.margin[0] + ')';
  }
  getXScale() {
    this.xDomain = this.getXDomain();
    const spacing = this.xDomain.length / (this.dims.width / this.barPadding + 1);
    return scaleBand()
      .rangeRound([0, this.dims.width])
      .paddingInner(spacing)
      .domain(this.xDomain);
  }
  getYScale() {
    this.yDomain = this.getYDomain();
    const scale = scaleLinear()
      .range([this.dims.height, 0])
      .domain(this.yDomain);
    return this.roundDomains ? scale.nice() : scale;
  }
  getXDomain() {
    return this.results.map(function (d) { return d.name; });
  }
  getYDomain() {
    const values = this.results.map(function (d) { return d.value; });
    const min = Math.min.apply(Math, [0].concat(values));
    const max = Math.max.apply(Math, values);
    return [min, max];
  }
  onClick(data) {
    this.select.emit(data);
  }
  setColors() {
    let domain;
    if (this.schemeType === 'ordinal') {
      domain = this.xDomain;
    } else {
      domain = this.yDomain;
    }
    this.colors = new ColorHelper(this.scheme, this.schemeType, domain, this.customColors);
  }
  getLegendOptions() {
    const opts = {
      scaleType: this.schemeType,
      colors: undefined,
      domain: [],
      title: undefined
    };
    opts.domain = this.legendContents;
    opts.colors = this.colors;
    opts.title = this.legendTitle;
    return opts;
  }
  updateYAxisWidth(_a) {
    const width = _a.width;
    this.yAxisWidth = width;
    this.update();
  }
  updateXAxisHeight(_a) {
    const height = _a.height;
    this.xAxisHeight = height;
    this.update();
  }
  onActivate(item) {
    const idx = this.activeEntries.findIndex(function (d) {
      return d.name === item.name && d.value === item.value && d.series === item.series;
    });
    if (idx > -1) {
      return;
    }
    this.activeEntries = [item].concat(this.activeEntries);
    this.activate.emit({ value: item, entries: this.activeEntries });
  }
  onDeactivate(item) {
    const idx = this.activeEntries.findIndex(function (d) {
      return d.name === item.name && d.value === item.value && d.series === item.series;
    });
    this.activeEntries.splice(idx, 1);
    this.activeEntries = this.activeEntries.slice();
    this.deactivate.emit({ value: item, entries: this.activeEntries });
    this.update();
  }
}

